<ng-container *ngIf="enableDataTableAccessibility$ | async; else oldTemplate">
<div class="position-relative app-datatable">
  <span *ngIf="title" class="p-2">{{ title }}</span>
  <div class="header" aria-label="Table controls and filters">
    <!-- Bulk Actions Group -->
    <div role="group" aria-label="Bulk actions" *ngIf="selectedItems.length">
      <!-- Bulk Edit (Match Records) -->
      <app-action-button
        *ngIf="allowMatchRecords && page.size < 500"
        [ariaLabel]="'Bulk match selected records'"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Bulk Match"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm bg-primary"
        (click)="bulkMatch()"
        iconClass="dt-action-item icon-plug"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Bulk match {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>

      <!-- Bulk Edit (Update Records) -->
      <app-action-button
        *ngIf="isBulkUpdateEnable && page.size < 500"
        [ariaLabel]="'Bulk edit selected records'"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Bulk Edit"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm ml-1 bg-primary"
        (click)="allowColumnFilter ? bulkUpdateRecords() : bulkUpdate()"
        iconClass="dt-action-item icon-edit-fill"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Bulk edit {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>

      <!-- Delete Selected -->
      <app-action-button
        *ngIf="isBulkDeleteEnable"
        [ariaLabel]="'Delete selected records'"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Delete selected"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm ml-1 bg-danger"
        (click)="deleteSelected()"
        iconClass="dt-action-item icon-delete-fill"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Delete {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>
    </div>

    <!-- Group By -->
    <div role="group" aria-label="Grouping controls" class="btn-group ml-1" *ngIf="!hideGrouping">
      <app-action-button
        class="no-bg-button btn btn-sm d-inline-block"
        style="height: fit-content; width: fit-content"
        [ngClass]="{'btn-enable': isColumnGroupingEnable}"
        [ariaLabel]="'Toggle row grouping'"
        [attr.aria-pressed]="isColumnGroupingEnable"
        matTooltip="Group by"
        [extraClasses]="isColumnGroupingEnable ? 'btn-enable':''"
        (click)="onColumnGroupingEnebled()"
        iconClass="dt-action-item fluent-GroupList"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnGroupingEnable ? 'Grouping enabled' : 'Grouping disabled' }}
        </app-sr-only>
      </app-action-button>
      <div
        class="btn-group"
        ngbDropdown
        #groupDropdown="ngbDropdown"
        role="group"
        style="height: fit-content; width: fit-content"
        aria-label="Group by column selection"
      >
        <button
          [disabled]="!isColumnGroupingEnable"
          [attr.aria-disabled]="!isColumnGroupingEnable"
          style="margin-left: -4px"
          type="button"
          class="no-bg-button btn btn-sm dropdown-toggle-split"
          ngbDropdownToggle
          aria-label="Select column to group by"
          aria-haspopup="listbox"
          [attr.aria-expanded]="groupDropdown.isOpen()"
          [attr.aria-controls]="'group-column-listbox'"
          matTooltip="Select Column"
        ></button>
        <div
          ngbDropdownMenu
          id="group-column-listbox"
          role="listbox"
          aria-label="Available columns for grouping"
          class="selectable datatable-dropdown-menu"
        >
          <button
            *ngFor="let column of visibleColumns; trackBy: trackByColumn"
            type="button"
            role="option"
            [attr.aria-selected]="getSelectedColIndex(column.prop) === payloadSortBy[0]?.column"
            (click)="toggleGroupingColumn(column.prop)"
            ngbDropdownItem
          >
            <ng-container *ngIf="column.name !== 'id'">
              <i
                *ngIf="getSelectedColIndex(column.prop) === payloadSortBy[0]?.column"
                class="icon-check"
                aria-hidden="true"
              ></i>
              <span>{{ column.name | titlecase }}</span>
            </ng-container>
          </button>
        </div>
      </div>
    </div>

    <!-- Column Search -->
    <app-action-button
      [ariaLabel]="'Enable column search, then navigate to a column to filter'"
      [attr.aria-pressed]="isColumnSearch"
      [matTooltip]="search.disabled ? 'Column search is not available' : isColumnSearch ? 'Clear Filters' : 'Column Search'"
      class="no-bg-button btn btn-sm d-inline-block"
      [ngClass]="{'btn-enable': isColumnSearch}"
      [extraClasses]="isColumnSearch ? 'btn-enable':''"
      (click)="toggleColumnSearch()"
      style="height: fit-content; width: fit-content"
        iconClass="dt-action-item icon-search"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnSearch ? 'Column search enabled' : 'Column search disabled' }}
        </app-sr-only>
      </app-action-button>

    <!-- Column Filter -->
    <app-action-button
      *ngIf="enableFilterSearch"
      [ariaLabel]="'Enable column filter, then navigate to a column to filter'"
      [attr.aria-pressed]="isColumnFilter"
      [matTooltip]="search.filterdisabled ? 'Column Filter is not available' : isColumnFilter ? 'Clear Filters' : 'Column Filter'"
      class="no-bg-button btn btn-sm"
      style="height: fit-content; width: fit-content"
      [extraClasses]="isColumnFilter ? 'btn-enable':''"
      [ngClass]="{'btn-enable': isColumnFilter}"
      (click)="toggleColumnFilter()"
        iconClass="dt-action-item icon-filter-list"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnFilter ? 'Column filter enabled' : 'Column filter disabled' }}
        </app-sr-only>
      </app-action-button>

    <!-- Entries Dropdown -->
    <div ngbDropdown *ngIf="showEnteriesDropdown" #entriesDropdown="ngbDropdown">
      <app-action-button
        matTooltip="Entries"
        id="page-size"
        [ariaLabel]="'Select rows per page'"
        aria-haspopup="listbox"
        [attr.aria-expanded]="entriesDropdown.isOpen()"
        aria-controls="page-size-listbox"
        style="height: fit-content; width: fit-content"
        ngbDropdownToggle
        class="no-bg-button btn btn-sm"
        iconClass="dt-action-item fluent-TextBulletListAdd"
        iconStyle="font-size:25px"
      >
        <span [attr.aria-label]="'Current page size: ' + page.size + ' rows'">({{ page.size }})</span>
      </app-action-button>
      <div
        ngbDropdownMenu
        id="page-size-listbox"
        role="listbox"
        aria-labelledby="page-size"
        class="selectable datatable-dropdown-menu"
      >
        <button
          *ngFor="let size of pageSizeOptions; trackBy: trackBySize"
          (click)="onPageSizeSelect(size)"
          type="button"
          role="option"
          [attr.aria-selected]="size === page.size"
          ngbDropdownItem
        >
          <i *ngIf="size === page.size" class="icon-check" aria-hidden="true"></i>
          <span>{{ size }}</span>
        </button>
      </div>
    </div>

    <!-- Export Dropdown -->
    <div ngbDropdown *ngIf="showExport" #exportDropdown="ngbDropdown">
      <app-action-button
        matTooltip="Export"
        id="export"
        [ariaLabel]="'Export table data'"
        aria-haspopup="menu"
        [attr.aria-expanded]="exportDropdown.isOpen()"
        aria-controls="export-menu"
        ngbDropdownToggle
        class="no-bg-button btn btn-sm"
        style="height: fit-content; width: fit-content"
        iconClass="dt-action-item fluent-ArrowDownload"
        iconStyle="font-size:25px"
      >
      </app-action-button>
      <div
        ngbDropdownMenu
        id="export-menu"
        role="menu"
        aria-labelledby="export"
        class="datatable-dropdown-menu"
      >
        <button
          *ngFor="let option of exportOptions; trackBy: trackByOption"
          (click)="onExport(option)"
          type="button"
          role="menuitem"
          ngbDropdownItem
        >
          {{ option }}
        </button>
      </div>
    </div>
  </div>

  <ngx-datatable
    *ngIf="renderTable" @fadeInOut
    #myTable
    [attr.data-table-id]="tableInstanceId"
    [attr.aria-label]="title || 'Data table'"
    [attr.aria-rowcount]="page.totalElements"
    [attr.aria-colcount]="visibleColumns?.length"
    [ngClass]="{'custom-style': isWhitespaces && !isEmbeddedReport}"
    class="material fullscreen"
    [columnMode]="columnMode"
    [headerHeight]="(isColumnFilter || isColumnSearch) ? 85 : 50"
    [groupRowsBy]="groupByName"
    [footerHeight]="50"
    [rowHeight]="rowHeight"
    [scrollbarV]="true"
    [scrollbarH]="true"
    [rows]="rows"
    [virtualization]="isVirtualizationEnabled ? true : false"
    [externalPaging]="externalPaging"
    [externalSorting]="externalSorting"
    [count]="(page.totalElements > page.size && isVirtualizationEnabled) ? page.size : page.totalElements"
    [offset]="page.pageNumber"
    [limit]="page.size"
    (page)="isVirtualizationEnabled ? null : pageListener($event)"
    (sort)="sortListener($event)"
    (select)="onSelect($event)"
    [selectionType]="SelectionType"
    [rowClass]="getRowClass"
    [summaryRow]="enableSummaryRow"
    [summaryHeight]="rowHeight"
    [summaryPosition]="summaryPositon"
    [displayCheck]="isCheckboxEnabled"
    [loadingIndicator]="loadingState || !renderColumn"
    (scroll)="onDatatableScroll($event)"
    [trackByProp]="'id'"
    >
    <ngx-datatable-group-header *ngIf="isGrouping" [rowHeight]="100">
      <ng-template
        let-group="group"
        let-expanded="expanded"
        ngx-datatable-group-header-template
      >
        <div style="padding-left: 5px" *ngIf="!!group.value">
          <button
            type="button"
            [class.datatable-icon-right]="!expanded"
            [class.datatable-icon-down]="expanded"
            [attr.aria-label]="(expanded ? 'Collapse' : 'Expand') + ' group ' + groupingTitleContent(payloadColumns[payloadSortBy[0].column]?.data, summary, group)"
            [attr.aria-expanded]="expanded"
            (click)="toggleExpandGroup(group)"
            (keydown.Enter)="$event.preventDefault(); toggleExpandGroup(group)"
            (keydown.space)="$event.preventDefault(); toggleExpandGroup(group)"
            class="group-expand-button"
          >
            {{
              fieldToTitle(
                payloadColumns[payloadSortBy[0].column]?.data
                  ? payloadColumns[payloadSortBy[0].column]?.data
                  : (summary?.GroupBy || embeddedGroupColumn)
              ) | titlecase
            }}:
            <b class="grouping-text" [innerHTML]="groupingTitleContent(payloadColumns[payloadSortBy[0].column]?.data, summary, group)"></b>
          </button>
          <div *ngIf="!expanded" class="row-data">
            <ng-container *ngFor="let item of group?.value; trackBy: trackByItem">
              <div class="row-data selectedRow" *ngIf="item?.isgrouped == 'true'">
                <div>Total ({{ item?.tot ? (item?.tot + ' records') : 'records' }})</div>
                <div class="row-data cell-width" *ngFor="let key of Object.keys(item); trackBy: trackByKey">
                  <div *ngIf="key != 'isgrouped'" [innerHTML]="item[key] ? item[key] : '-'"></div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>
      </ng-template>
    </ngx-datatable-group-header>

    <!-- Single Select Checkbox Column -->
    <ngx-datatable-column
      *ngIf="singleSelect"
      [width]="15"
      [maxWidth]="20"
      [checkboxable]="true"
      [headerCheckboxable]="false"
    >
      <ng-template ngx-datatable-cell-template let-row="row" let-rowIndex="rowIndex">
        <input
          type="checkbox"
          [checked]="singleSelectRow === row.id"
          appRowCheckbox
          [rowIndex]="rowIndex"
          [tableId]="tableInstanceId"
        />
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable) && !isGrouping && !isPrintMode"
      [width]="30"
      [maxWidth]="40"
      [sortable]="false"
      [draggable]="false"
      [resizeable]="false"
      [frozenLeft]="pinActions"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template ngx-datatable-header-template let-value="value" let-allRowsSelected="allRowsSelected" let-selectFn="selectFn">
        <input
          #selectAllCheckbox
          id="selectAllCheckbox"
          type="checkbox"
          [checked]="allRowsSelected"
          (change)="selectFn(!allRowsSelected)"
          aria-label="Select all rows"
        />
      </ng-template>
      <ng-template ngx-datatable-cell-template let-row="row" let-rowIndex="rowIndex" let-isSelected="isSelected" let-onCheckboxChangeFn="onCheckboxChangeFn">
        <input
          type="checkbox"
          [checked]="isSelected"
          (change)="onCheckboxChangeFn($event)"
          appRowCheckbox
          [rowIndex]="rowIndex"
          [tableId]="tableInstanceId"
        />
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="actions && !isPrintMode"
      name="Actions"
      [resizeable]="false"
      [frozenLeft]="pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="actionColumnWidth"
      [width]="actionColumnWidth"
      [checkboxable]="!customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable) && !isGrouping"
      [headerCheckboxable]="!customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable)"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: actions, reportId: dataTableReportId, tableId: tableId }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="impersonate"
      name="Impersonate User"
      [frozenLeft]="commonService.is_small_screen ? false : pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="140"
      [width]="140"
      [checkboxable]="false"
      [headerClass]="'wrap-header'"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: impersonate }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="notificationStatus"
      name="Status"
      [frozenLeft]="commonService.is_small_screen ? false : pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="100"
      [width]="100"
      [minWidth]="100"
      [checkboxable]="false"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: notificationStatus }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="passwordReset"
      name="Password Reset"
      [frozenLeft]="pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="140"
      [width]="140"
      [checkboxable]="false"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header wrap-header' : 'wrap-header'"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: passwordReset }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>

    <!-- Other Columns -->
    <ng-container *ngIf="renderColumn">
      <ngx-datatable-column
        *ngFor="let col of visibleColumns; let colIdx = index; trackBy: trackByColumn"
        [name]="fromRecordTable ? col.name : (col.name | titlecase)"
        [prop]="col.prop || (col.name | lowercase)"
        [width]="getColWidth(col)"
        [minWidth]="getColMinWidth(col)"
        [comparator]="noClientSort"
        [summaryFunc]="enableSummaryRow && (col.fieldType == 'INT' || col.fieldType === 'DECIMAL(20,2)' || col.fieldType === 'DOUBLE') ? (summaryFunction == 'sum' ? totalSum : summaryFunction == 'avg' ? columnsAvg : totalCount) : nullSummary"
      >
        <ng-template
          let-column="column"
          let-row="row"
          let-value="value"
          let-sort="sortFn"
          ngx-datatable-header-template
        >
          <div style="display: flex; flex-direction: column; width: 100%;">
            <div class="sort-header-wrapper" [attr.aria-label]="getSortAriaValue(column.prop, column.name)" [ngStyle]="{'justify-content': col.valueAlign}">
              <span class="column-name" (click)="sort()">{{ column.name }}</span>
              <button
                type="button"
                class="sort-icon-button"
                (click)="sort()"
                [attr.aria-label]="'Sort by ' + column.name + ', currently ' + getSortStateText(column.prop)"
              >
                <span
                  class="sort-icon"
                  aria-hidden="true"
                  [ngClass]="sortingColumn[column.prop] === 'asc' ? 'sort-asc datatable-icon-up' : (sortingColumn[column.prop] === 'desc' ? 'datatable-icon-down sort-desc' : 'datatable-icon-sort-unset')"
                ></span>
                <app-sr-only>{{ getSortStateText(column.prop) }}</app-sr-only>
              </button>
            </div>
            <ng-container *ngIf="isColumnSearch">
              <ng-container *ngIf="col.fieldType === 'PII2' || col.fieldType === 'PII' || col.proxyFieldType === 'PII2' || col.proxyFieldType === 'PII'">
                <span *ngIf="!col.allowDecrypt && !col.proxyDecryptionAllowed" style="margin-top: 10px; color: #ffd8d8;" role="alert">Search Not Allowed</span>
                <ng-container *ngIf="col.allowDecrypt || col.proxyDecryptionAllowed">
                  <label [for]="'search-' + col.fieldName">
                    <app-sr-only>Search {{ col.name }}</app-sr-only>
                  </label>
                  <ng-container *ngTemplateOutlet="inputSelector(col); context: { column: column, value: value, row: row, col: col }"></ng-container>
                </ng-container>
              </ng-container>
              <ng-container *ngIf="col.fieldType !== 'PII2' && col.fieldType !== 'PII' && col.proxyFieldType !== 'PII2' && col.proxyFieldType !== 'PII'">
                <label [for]="'search-' + col.fieldName">
                  <app-sr-only>Search {{ col.name }}</app-sr-only>
                </label>
                <ng-container *ngTemplateOutlet="inputSelector(col); context: { column: column, value: value, row: row, col: col }"></ng-container>
              </ng-container>
            </ng-container>
            <ng-container *ngIf="isColumnFilter">
              <ng-container *ngIf="col.fieldType === 'PII2' || col.fieldType === 'PII' || col.proxyFieldType === 'PII2' || col.proxyFieldType === 'PII' || (col.fieldName === 'throttling' && allowColumnFilter)">
                <span style="margin-top: 10px; color: #ffd8d8;" role="alert">Filter Not Allowed</span>
              </ng-container>
              <ng-container *ngIf="col.fieldType !== 'PII2' && col.fieldType !== 'PII' && col.proxyFieldType !== 'PII2' && col.proxyFieldType !== 'PII' && !(col.fieldName === 'throttling' && allowColumnFilter)">
                <button
                  type="button"
                  #filterBtn
                  [id]="'filter-' + col.fieldName"
                  [attr.aria-label]="'Filter ' + col.name + (isFilterSelected(col) ? ', active' : '')"
                  [attr.aria-pressed]="isFilterSelected(col)"
                  [ngStyle]="{'background-color': isFilterSelected(col) ? 'var(--opengov-primary-blurple)' : ''}"
                  [ngbPopover]="filterBox"
                  triggers="manual"
                  #t="ngbPopover"
                  placement="bottom-left top-left bottom-right top-right"
                  [autoClose]="false"
                  popoverClass="custom-popup"
                  container="body"
                  class="btn btn-outline-secondary rounded filter-button template-button d-flex justify-content-center align-items-center"
                  (click)="onOverLayOpen($event, t, column, value, row, col)"
                  (shown)="onFilterPopoverShown(filterBtn)"
                  (hidden)="onFilterPopoverHidden(filterBtn)"
                  aria-haspopup="dialog"
                  [attr.aria-expanded]="t.isOpen()"
                >
                  <i
                    class="icon-filter-list"
                    style="font-size:25px; position:relative; left:0px"
                    [ngStyle]="{'color': isFilterSelected(col) ? 'var(--foreground-ko)' : (isLightHeader ? 'var(--filter-btn-foreground)' :'var(--foreground-ko)')}"
                    aria-hidden="true"
                  ></i>
                  <app-sr-only>Filter {{ col.name }}</app-sr-only>
                </button>
              </ng-container>
            </ng-container>
          </div>
        </ng-template>
        <ng-template
          let-column="column"
          let-row="row"
          let-value="value"
          let-rowIndex="rowIndex"
          ngx-datatable-cell-template
        >
          <ng-container *ngIf="loadingState">
            <div class="skeleton-cell"></div>
          </ng-container>
          <ng-container *ngIf="!loadingState && rows.length">
            <ng-container
              *ngTemplateOutlet="universalCell;
                context: { column: column, value: value, row: row, col: col, rowIndex: rowIndex, colIdx: colIdx }"
            ></ng-container>
          </ng-container>
        </ng-template>
      </ngx-datatable-column>
    </ng-container>

    <ngx-datatable-footer>
      <ng-template
        ngx-datatable-footer-template
        let-rowCount="rowCount"
        let-pageSize="pageSize"
        let-selectedCount="selectedCount"
        let-curPage="curPage"
        let-offset="offset"
        let-isVisible="isVisible"
      >
        <div class="page-count">
          {{ footerText }}
          <ng-container *ngIf="selectedItems.length">
            ({{ selectedItems.length }} selected)
          </ng-container>
        </div>
        <!-- Custom accessible pagination -->
        <app-datatable-pagination
          *ngIf="showEnteriesDropdown && customPagination"
          [currentPage]="currentPageNumber + 1"
          [totalItems]="isVirtualizationEnabled ? page.totalElements : rowCount"
          [pageSize]="isVirtualizationEnabled ? page.size : pageSize"
          (pageChange)="onPageChange($event)"
        ></app-datatable-pagination>

        <!-- Original ngx-datatable pager -->
        <datatable-pager
          *ngIf="showEnteriesDropdown && !customPagination"
          [pagerLeftArrowIcon]="'datatable-icon-left'"
          [pagerRightArrowIcon]="'datatable-icon-right'"
          [pagerPreviousIcon]="'datatable-icon-prev'"
          [pagerNextIcon]="'datatable-icon-skip'"
          [page]="currentPageNumber + 1"
          [size]="isVirtualizationEnabled ? page.size : pageSize"
          [count]="isVirtualizationEnabled ? page.totalElements : rowCount"
          [hidden]="isVirtualizationEnabled ? !(page.totalElements / page.size > 1) : !(rowCount / pageSize > 1)"
          (change)="isVirtualizationEnabled ? pageListener($event) : table.onFooterPage($event)"
        ></datatable-pager>
      </ng-template>
    </ngx-datatable-footer>
  </ngx-datatable>
</div>

<!-- Universal Cell Template -->
<ng-template #universalCell let-column="column" let-value="value" let-row="row" let-col="col" let-rowIndex="rowIndex" let-colIdx="colIdx">
  <div
    appDatatableCell
    [attr.data-row-index]="rowIndex || 0"
    [attr.data-col-index]="colIdx ?? 0"
    [attr.data-table-id]="tableInstanceId"
    [rowIndex]="rowIndex || 0"
    [colIndex]="colIdx ?? 0"
    [columnName]="col?.name || column?.name || 'Unknown'"
    [cellValue]="value"
    [tableId]="tableInstanceId"
    [isFileLink]="col.isFile && value"
    [isExternalLink]="col.isHyperLink && col.isUrl && value"
    [isLink]="col.isHyperLink && !col.isUrl && !col.isEmail && value"
    [isEmailLink]="col.isEmail && value"
    [isEncrypted]="(col.fieldType === 'PII2' || col.proxyFieldType === 'PII2') && value"
  >
  <ng-container *ngIf="col.isHyperLink">
    <div
      *ngIf="value != null && value != '' && col.isUrl"
      class="w-100"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
    >
      <a
        [href]="routeLink(row, column)"
        target="_blank"
        tabindex="-1"
        [ngClass]="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) ? 'hovered-link' : ''"
      >
        {{ linkText(row, column) }}
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <div
      *ngIf="value != null && value != '' && !col.isUrl && col.fieldType !== 'PII2' && col.proxyFieldType !== 'PII2' && !col.isFile"
      [ngClass]="col.isFile ? '' : 'w-100'"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
    >
      <a
        [routerLink]="routeLink(row, column)"
        tabindex="-1"
        [ngClass]="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && col.isRelation) ? 'hovered-link' : ''"
      >
        <ng-container *ngIf="col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && col.isRelation">
          {{ getValue(column, value, row) }}
        </ng-container>
        <ng-container *ngIf="!(col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') || !col.isRelation">
          <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
        </ng-container>
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && col.isRelation) && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <a
      *ngIf="value != null && value != '' && col.isFile"
      class="link"
      tabindex="-1"
      (click)="fileLink(row, column, col)"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </a>
    <div
      *ngIf="value != null && value != '' && !col.isUrl && !col.isFile && (col.fieldType === 'PII2' || col.proxyFieldType === 'PII2')"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
    <div *ngIf="value == null || value == ''" class="w-100">
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
  </ng-container>
  <ng-container *ngIf="col.isFile">
    <ng-container *ngIf="value != null && value != '' && col.isHyperLink">
      <div class="d-flex">
        <a
          title="View Document"
          aria-label="View Document"
          target="_blank"
          tabindex="-1"
          (click)="fileLink(row, column, col)"
          class="d-flex align-items-center mx-1"
        >
          <i class="icon-view"></i>
        </a>
        <a
          title="Download Document"
          aria-label="Download Document"
          target="_blank"
          tabindex="-1"
          (click)="fileLink(row, column, col, true)"
          class="d-flex align-items-center mx-1"
        >
          <i class="icon-download"></i>
        </a>
      </div>
    </ng-container>
    <a
      *ngIf="value != null && value != '' && !col.isHyperLink"
      class="link"
      tabindex="-1"
      (click)="fileLink(row, column, col)"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </a>
    <div
      *ngIf="(value == null || value == '') && !col.isHyperLink"
      class="w-100"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
  </ng-container>
  <ng-container *ngIf="!col.isHyperLink && !col.isFile">
    <ng-container *ngIf="selectTemplate(value, column, col)">
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </ng-container>
  </ng-container>
  </div>
</ng-template>

<!-- Custom Templates -->
<ng-template #invitedUsers let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="invite-users">
    <i class="muted">Invitation sent</i>
  </div>
</ng-template>

<ng-template #activeUsers let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="active-users">
    <app-checkbox
      type="toggle"
      [checked]="value === 'True'"
      (change)="openActivateUserModal($event, row)"
    ></app-checkbox>
  </div>
</ng-template>

<ng-template #emailAlerts let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="active-users">
    <app-checkbox
      *ngIf="row.active !== 'Invited'"
      type="toggle"
      [checked]="value === 'True'"
      (change)="OnEmailAlerts($event, row)"
    ></app-checkbox>
    <div *ngIf="row.active == 'Invited'">-</div>
  </div>
</ng-template>

<ng-template #checkbox let-column="column" let-value="value" let-col="col" let-row="row">
  <div
    class="custom-control custom-checkbox custom-checkbox-new w-100 custom-checkbox-un-01"
    (mouseenter)="onMouseEnter(col, row)"
    (mouseleave)="onMouseLeave()"
  >
    <input
      [checked]="isCheckboxChecked(value)"
      type="checkbox"
      class="custom-control-input"
    />
    <label class="custom-control-label custom-checkbox-label custom-table-checkbox"></label>
    <button
      *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
      type="button"
      tabindex="-1"
      [ngbPopover]="inlineEditor"
      triggers="manual"
      #p="ngbPopover"
      [autoClose]="false"
      popoverClass="custom-popup"
      container="body"
      (click)="openEditModal(p, col, row, value)"
      class="btn btn-sm edit-button"
    >
      <i class="icon-edit"></i>
    </button>
  </div>
</ng-template>

<ng-template #default let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    *ngIf="!col.isHtmlallowed"
    [ngStyle]="{'textAlign': col.valueAlign}"
    class="w-100"
    [matTooltip]="getValue(column, value, row)"
    (mouseenter)="onMouseEnter(col, row)"
    (mouseleave)="onMouseLeave()"
  >
    <ng-container *ngIf="col.isEmail">
      <a
        (click)="handleMailClick(getValue(column, value, row))"
        tabindex="-1"
        [ngClass]="{
          'hovered-link email-link': (col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id),
          'active-email': getValue(column, value, row),
          'no-email': !getValue(column, value, row)
        }"
      >
        {{ getValue(column, value, row) || '-' }}
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </ng-container>
    <ng-container *ngIf="!col.isEmail">
      <span
        [ngClass]="{'hovered-link': (col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)}"
      >
        {{ getValue(column, value, row) || '-' }}
      </span>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </ng-container>
  </div>
  <div
    *ngIf="col.isHtmlallowed"
    [ngStyle]="{'textAlign': col.valueAlign}"
    [innerHTML]="getValue(column, value, row) | safeHTML"
  ></div>
</ng-template>

<ng-template #fieldsreports let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    [ngStyle]="{'textAlign': col.valueAlign}"
    [matTooltip]="getValue(column, value, row)"
    [class]="getColumnClass(column, value, row)"
  >
    {{ getValue(column, value, row) }}
  </div>
</ng-template>

<ng-template #updateActiveTemplate let-modal>
  <div class="modal-content">
    <div class="modal-header">
      <div class="modal-header-left">
        <h4 class="modal-title">Activate/Deactivate User</h4>
      </div>
      <div class="modal-header-right">
        <button type="button" class="close" (click)="modal.close()">
          <i class="icon-close"></i>
        </button>
      </div>
    </div>
    <div class="modal-body">
      <div class="modal-body-inner">
        <button class="btn btn-dark mr-4" type="button" (click)="updateActivateUser(false)">{{buttonText}} user for this App</button>
        <button class="btn btn-dark" type="button" (click)="updateActivateUser(true)">{{buttonText}} user for all Apps</button>
      </div>
    </div>
    <div class="modal-footer">
      <div class="modal-footer-bottom">
        <app-modal-action-btns
          (onReject)="modal.close()"
          [showSave]="false"
        ></app-modal-action-btns>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #specialColumnTemplate let-row="row" let-rowIndex="rowIndex" let-columnTemplate="columnTemplate" let-reportId="reportId" let-tableId="tableId">
  <div
    appActionContainer
    [rowIndex]="rowIndex"
    [tableId]="tableInstanceId"
  >
    <ng-container *ngIf="loadingState">
      <div class="skeleton-cell"></div>
    </ng-container>
    <ng-container *ngIf="!loadingState">
      <ng-container
        *ngIf="(groupedByColumn || summary?.GroupBy || embeddedGroupColumn) && row?.isgrouped == 'true'"
      >
        Total ({{ row?.tot ? (row?.tot + ' records') : 'records' }})
      </ng-container>
      <ng-container
        *ngIf="!(groupedByColumn || summary?.GroupBy || embeddedGroupColumn) || row?.isgrouped == 'false'"
      >
        <ng-container
          *ngTemplateOutlet="columnTemplate; context: { row: row, rowIndex: rowIndex, reportId: reportId, tableId: tableId, currentPage: currentPageNumber, pageSize: page.size }"
        ></ng-container>
      </ng-container>
    </ng-container>
  </div>
</ng-template>

<ng-template #searchInput let-column="column" let-value="value" let-row="row" let-col="col">
  <input
    *ngIf="col.fieldType !== 'VARCHAR(15)'"
    [id]="'search-' + col.fieldName"
    style="height: 43px;"
    (keyup)="onColumnSearch($event, column, col)"
    (change)="onColumnSearch($event, column, col)"
    placeholder="{{ isColumnFilter ? 'Filter' : 'Search' }} {{ column.name }}"
    class="col-search-ip form-control"
    [ngClass]="{'is-invalid': dirty.includes(column.name)}"
    [type]="inputFieldType(col)"
  />
  <input
    *ngIf="col.fieldType === 'VARCHAR(15)'"
    [id]="'search-' + col.fieldName"
    (keyup.enter)="currentPageNumber = 0; emitPayload()"
    style="height: 43px;"
    (keyup)="onColumnSearch($event, column, col)"
    placeholder="{{ isColumnFilter ? 'Filter' : 'Search' }} {{ column.name }}"
    class="col-search-ip"
    type="text"
    [mask]="col.fieldType === 'VARCHAR(15)' ? '(000) 000-0000' : ''"
  />
</ng-template>

<ng-template #selectSearchScroll let-column="column" let-value="value" let-row="row" let-col="col">
  <app-select-search-scroll
    [minSearchLength]="3"
    [searchable]="true"
    [scrollable]="true"
    class="w-100"
    (selected)="onColumnSearch($event, column, col, true)"
    [isConditionComponent]="(col.isRelation || loadExternalUsers) ? false : true"
    [isSubscriptionComponent]="loadExternalUsers ? true : false"
    [isRelatedDataComponent]="col.isRelation ? true : false"
    [fetchFromServer]="col.isRelation"
    [passedTableKey]="tableId"
    [isSelectDisabled]="true"
    [dropdownFieldKey]="col.isRelation ? col.proxyFieldKey : ''"
    [dropdownType]="col.proxyFieldType"
    [multiple]="isColumnFilter ? true : false"
    [closeOnSelect]="isColumnFilter ? false : true"
    [ngStyle]="{'margin-top': isEmbeddedReport && isInDetailsComponent ? '-8px' : ''}"
    [relatedFieldKey]="col.fieldKey"
  ></app-select-search-scroll>
</ng-template>

<ng-template #enumInput let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    class="form-group select2-form-group w-100"
    style="margin-bottom: 43px;"
    [ngStyle]="{'margin-top': isEmbeddedReport && isInDetailsComponent ? '-8px' : ''}"
  >
    <ng-select
      bindLabel="label"
      bindValue="value"
      [items]="col.dropdownValues"
      [multiple]="(col.fieldType === 'ENUM2' || isColumnFilter) ? true : false"
      appendTo="body"
      (change)="selectSelected($event, column, col)"
      placeholder="Search"
      [closeOnSelect]="isColumnFilter ? false : true"
    >
      <ng-template ng-option-tmp let-item="item">
        {{ item.label }}
      </ng-template>
    </ng-select>
  </div>
</ng-template>

<ng-template #encryptedField let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="d-flex align-items-center w-100">
    <i
      class="icon-view-fill"
      *ngIf="value && (row[col.fieldName + '_decrypted'] || row[col.fieldName + '_proxy_decrypted'])"
      (click)="decryptEncryptField($event, col, row, value)"
      (keydown)="$event.preventDefault()"
    ></i>
    <div
      *ngIf="!col.isHyperLink || !value"
      id="encrypted-{{ col.fieldKey }}-{{ row.id }}"
      [ngStyle]="{'textAlign': col.valueAlign}"
      [matTooltip]="getValue(column, value, row)"
      [class]="getColumnClass(column, value, row)"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
      [ngStyle]="{'width': ((allowInlineEdit && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && !col.isHyperLink && value) ? '100%' : 'auto'}"
    >
      <span
        [ngClass]="((allowInlineEdit && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && !col.isHyperLink) ? 'hovered-link' : ''"
      >{{ getValue(column, value, row) || '-' }}</span>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <a
      *ngIf="col.isHyperLink && value"
      id="encrypted-{{ col.fieldKey }}-{{ row.id }}"
      [ngStyle]="{'textAlign': col.valueAlign}"
      [routerLink]="routeLink(row, column)"
      tabindex="-1"
      [matTooltip]="getValue(column, value, row)"
      [class]="getColumnClass(column, value, row)"
    >
      {{ getValue(column, value, row) || '-' }}
    </a>
  </div>
</ng-template>

<ng-template #filterBox>
  <app-column-filter
  [filterCol]="filterCol"
  [tableId]="tableId"
  [loadExternalUsers]="loadExternalUsers"
  [allowColumnFilter]="allowColumnFilter"
  (onFilterApply)="selectFilterSelected($event)"
  (onFilterClear)="clearFilterForCol($event)"
></app-column-filter>
</ng-template>

<ng-template #inlineEditor>
  <app-inline-editor
    [col]="editColumn"
    [row]="editRow"
    [value]="editValue"
    [tableId]="tableId"
    (editSubmit)="onEditSubmit($event)"
    (editCancel)="onEditCancel()"
  ></app-inline-editor>
</ng-template>

<!-- Screen reader announcements -->
<div
  role="status"
  aria-live="polite"
  aria-atomic="true"
>
  <app-sr-only>{{ screenReaderMessage }}</app-sr-only>
</div>
</ng-container>

<ng-template #oldTemplate>
<div class="position-relative app-datatable">
  <span *ngIf="title" class="p-2">{{ title }}</span>
  <div class="header" aria-label="Table controls and filters">
    <!-- Bulk Actions Group -->
    <div role="group" aria-label="Bulk actions" *ngIf="selectedItems.length">
      <!-- Bulk Edit (Match Records) -->
      <app-action-button
        *ngIf="allowMatchRecords && page.size < 500"
        aria-label="Bulk match selected records"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Bulk Match"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm bg-primary"
        (click)="bulkMatch()"
        iconClass="dt-action-item icon-plug"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Bulk match {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>

      <!-- Bulk Edit (Update Records) -->
      <app-action-button
        *ngIf="isBulkUpdateEnable && page.size < 500"
        aria-label="Bulk edit selected records"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Bulk Edit"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm ml-1 bg-primary"
        (click)="allowColumnFilter ? bulkUpdateRecords() : bulkUpdate()"
        iconClass="dt-action-item icon-edit-fill"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Bulk edit {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>

      <!-- Delete Selected -->
      <app-action-button
        *ngIf="isBulkDeleteEnable"
        aria-label="Delete selected records"
        [attr.aria-disabled]="selectedItems.length === 0"
        matTooltip="Delete selected"
        style="height: fit-content; width: fit-content"
        class="no-bg-button btn btn-sm ml-1 bg-danger"
        (click)="deleteSelected()"
        iconClass="dt-action-item icon-delete-fill"
        iconStyle="color:var(--foreground-ko)"
      >
        <app-sr-only>Delete {{ selectedItems.length }} selected records</app-sr-only>
      </app-action-button>
    </div>

    <!-- Group By -->
    <div role="group" aria-label="Grouping controls" class="btn-group ml-1" *ngIf="!hideGrouping">
      <app-action-button
        class="no-bg-button btn btn-sm d-inline-block"
        style="height: fit-content; width: fit-content"
        [ngClass]="{'btn-enable': isColumnGroupingEnable}"
        aria-label="Toggle row grouping"
        [attr.aria-pressed]="isColumnGroupingEnable"
        matTooltip="Group by"
        [extraClasses]="isColumnGroupingEnable ? 'btn-enable':''"
        (click)="onColumnGroupingEnebled()"
        iconClass="dt-action-item fluent-GroupList"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnGroupingEnable ? 'Grouping enabled' : 'Grouping disabled' }}
        </app-sr-only>
      </app-action-button>
      <div
        class="btn-group"
        ngbDropdown
        #groupDropdown="ngbDropdown"
        role="group"
        style="height: fit-content; width: fit-content"
        aria-label="Group by column selection"
      >
        <button
          [disabled]="!isColumnGroupingEnable"
          [attr.aria-disabled]="!isColumnGroupingEnable"
          style="margin-left: -4px"
          type="button"
          class="no-bg-button btn btn-sm dropdown-toggle-split"
          ngbDropdownToggle
          aria-label="Select column to group by"
          aria-haspopup="listbox"
          [attr.aria-expanded]="groupDropdown.isOpen()"
          [attr.aria-controls]="'group-column-listbox'"
          matTooltip="Select Column"
        ></button>
        <div
          ngbDropdownMenu
          id="group-column-listbox"
          role="listbox"
          aria-label="Available columns for grouping"
          class="selectable datatable-dropdown-menu"
        >
          <button
            *ngFor="let column of visibleColumns; trackBy: trackByColumn"
            type="button"
            role="option"
            [attr.aria-selected]="getSelectedColIndex(column.prop) === payloadSortBy[0]?.column"
            (click)="toggleGroupingColumn(column.prop)"
            ngbDropdownItem
          >
            <ng-container *ngIf="column.name !== 'id'">
              <i
                *ngIf="getSelectedColIndex(column.prop) === payloadSortBy[0]?.column"
                class="icon-check"
                aria-hidden="true"
              ></i>
              <span>{{ column.name | titlecase }}</span>
            </ng-container>
          </button>
        </div>
      </div>
    </div>

    <!-- Column Search -->
    <app-action-button
      aria-label="Enable column search, then navigate to a column to filter"
      [attr.aria-pressed]="isColumnSearch"
      [matTooltip]="search.disabled ? 'Column search is not available' : isColumnSearch ? 'Clear Filters' : 'Column Search'"
      class="no-bg-button btn btn-sm d-inline-block"
      [ngClass]="{'btn-enable': isColumnSearch}"
      [extraClasses]="isColumnSearch ? 'btn-enable':''"
      (click)="toggleColumnSearch()"
      style="height: fit-content; width: fit-content"
        iconClass="dt-action-item icon-search"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnSearch ? 'Column search enabled' : 'Column search disabled' }}
        </app-sr-only>
      </app-action-button>

    <!-- Column Filter -->
    <app-action-button
      *ngIf="enableFilterSearch"
      aria-label="Enable column filter, then navigate to a column to filter"
      [attr.aria-pressed]="isColumnFilter"
      [matTooltip]="search.filterdisabled ? 'Column Filter is not available' : isColumnFilter ? 'Clear Filters' : 'Column Filter'"
      class="no-bg-button btn btn-sm"
      style="height: fit-content; width: fit-content"
      [extraClasses]="isColumnFilter ? 'btn-enable':''"
      [ngClass]="{'btn-enable': isColumnFilter}"
      (click)="toggleColumnFilter()"
        iconClass="dt-action-item icon-filter-list"
        iconStyle="font-size:25px"
      >
        <app-sr-only>
          {{ isColumnFilter ? 'Column filter enabled' : 'Column filter disabled' }}
        </app-sr-only>
      </app-action-button>

    <!-- Entries Dropdown -->
    <div ngbDropdown *ngIf="showEnteriesDropdown" #entriesDropdown="ngbDropdown">
      <app-action-button
        matTooltip="Entries"
        id="page-size"
        aria-label="Select rows per page"
        aria-haspopup="listbox"
        [attr.aria-expanded]="entriesDropdown.isOpen()"
        aria-controls="page-size-listbox"
        style="height: fit-content; width: fit-content"
        ngbDropdownToggle
        class="no-bg-button btn btn-sm"
        iconClass="dt-action-item fluent-TextBulletListAdd"
        iconStyle="font-size:25px"
      >
        <span [attr.aria-label]="'Current page size: ' + page.size + ' rows'">({{ page.size }})</span>
      </app-action-button>
      <div
        ngbDropdownMenu
        id="page-size-listbox"
        role="listbox"
        aria-labelledby="page-size"
        class="selectable datatable-dropdown-menu"
      >
        <button
          *ngFor="let size of pageSizeOptions; trackBy: trackBySize"
          (click)="onPageSizeSelect(size)"
          type="button"
          role="option"
          [attr.aria-selected]="size === page.size"
          ngbDropdownItem
        >
          <i *ngIf="size === page.size" class="icon-check" aria-hidden="true"></i>
          <span>{{ size }}</span>
        </button>
      </div>
    </div>

    <!-- Export Dropdown -->
    <div ngbDropdown *ngIf="showExport" #exportDropdown="ngbDropdown">
      <app-action-button
        matTooltip="Export"
        id="export"
        aria-label="Export table data"
        aria-haspopup="menu"
        [attr.aria-expanded]="exportDropdown.isOpen()"
        aria-controls="export-menu"
        ngbDropdownToggle
        class="no-bg-button btn btn-sm"
        style="height: fit-content; width: fit-content"
        iconClass="dt-action-item fluent-ArrowDownload"
        iconStyle="font-size:25px"
      >
      </app-action-button>
      <div
        ngbDropdownMenu
        id="export-menu"
        role="menu"
        aria-labelledby="export"
        class="datatable-dropdown-menu"
      >
        <button
          *ngFor="let option of exportOptions; trackBy: trackByOption"
          (click)="onExport(option)"
          type="button"
          role="menuitem"
          ngbDropdownItem
        >
          {{ option }}
        </button>
      </div>
    </div>
  </div>

  <ngx-datatable
    *ngIf="renderTable" @fadeInOut
    #myTable
    [attr.data-table-id]="tableInstanceId"
    role="grid"
    [attr.aria-label]="title || 'Data table'"
    [attr.aria-rowcount]="page.totalElements"
    [attr.aria-colcount]="visibleColumns?.length"
    [ngClass]="{'custom-style': isWhitespaces && !isEmbeddedReport}"
    class="material fullscreen"
    [columnMode]="columnMode"
    [headerHeight]="(isColumnFilter || isColumnSearch) ? 85 : 50"
    [groupRowsBy]="groupByName"
    [footerHeight]="50"
    [rowHeight]="rowHeight"
    [scrollbarV]="true"
    [scrollbarH]="true"
    [rows]="rows"
    [virtualization]="isVirtualizationEnabled ? true : false"
    [externalPaging]="externalPaging"
    [externalSorting]="externalSorting"
    [count]="(page.totalElements > page.size && isVirtualizationEnabled) ? page.size : page.totalElements"
    [offset]="page.pageNumber"
    [limit]="page.size"
    (page)="isVirtualizationEnabled ? null : pageListener($event)"
    (sort)="sortListener($event)"
    (select)="onSelect($event)"
    [selectionType]="SelectionType"
    [rowClass]="getRowClass"
    [summaryRow]="enableSummaryRow"
    [summaryHeight]="rowHeight"
    [summaryPosition]="summaryPositon"
    [displayCheck]="isCheckboxEnabled"
    [loadingIndicator]="loadingState || !renderColumn"
    (scroll)="onDatatableScroll($event)"
    [trackByProp]="'id'"
    >
    <ngx-datatable-group-header *ngIf="isGrouping" [rowHeight]="100">
      <ng-template
        let-group="group"
        let-expanded="expanded"
        ngx-datatable-group-header-template
      >
        <div style="padding-left: 5px" *ngIf="!!group.value">
          <button
            type="button"
            [class.datatable-icon-right]="!expanded"
            [class.datatable-icon-down]="expanded"
            [attr.aria-label]="(expanded ? 'Collapse' : 'Expand') + ' group ' + groupingTitleContent(payloadColumns[payloadSortBy[0].column]?.data, summary, group)"
            [attr.aria-expanded]="expanded"
            (click)="toggleExpandGroup(group)"
            (keydown.Enter)="$event.preventDefault(); toggleExpandGroup(group)"
            (keydown.space)="$event.preventDefault(); toggleExpandGroup(group)"
            class="group-expand-button"
          >
            {{
              fieldToTitle(
                payloadColumns[payloadSortBy[0].column]?.data
                  ? payloadColumns[payloadSortBy[0].column]?.data
                  : (summary?.GroupBy || embeddedGroupColumn)
              ) | titlecase
            }}:
            <b class="grouping-text" [innerHTML]="groupingTitleContent(payloadColumns[payloadSortBy[0].column]?.data, summary, group)"></b>
          </button>
          <div *ngIf="!expanded" class="row-data">
            <ng-container *ngFor="let item of group?.value; trackBy: trackByItem">
              <div class="row-data selectedRow" *ngIf="item?.isgrouped == 'true'">
                <div>Total ({{ item?.tot ? (item?.tot + ' records') : 'records' }})</div>
                <div class="row-data cell-width" *ngFor="let key of Object.keys(item); trackBy: trackByKey">
                  <div *ngIf="key != 'isgrouped'" [innerHTML]="item[key] ? item[key] : '-'"></div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>
      </ng-template>
    </ngx-datatable-group-header>

    <!-- Single Select Checkbox Column -->
    <ngx-datatable-column
      *ngIf="singleSelect"
      [width]="15"
      [maxWidth]="20"
      [checkboxable]="true"
      [headerCheckboxable]="false"
    >
      <ng-template ngx-datatable-cell-template let-row="row" let-rowIndex="rowIndex">
        <input
          type="checkbox"
          [checked]="singleSelectRow === row.id"
          appRowCheckbox
          [rowIndex]="rowIndex"
          [tableId]="tableInstanceId"
        />
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable) && !isGrouping && !isPrintMode"
      [width]="30"
      [maxWidth]="40"
      [sortable]="false"
      [draggable]="false"
      [resizeable]="false"
      [frozenLeft]="pinActions"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template ngx-datatable-header-template let-value="value" let-allRowsSelected="allRowsSelected" let-selectFn="selectFn">
        <input
          #selectAllCheckbox
          id="selectAllCheckbox"
          type="checkbox"
          [checked]="allRowsSelected"
          (change)="selectFn(!allRowsSelected)"
          aria-label="Select all rows"
        />
      </ng-template>
      <ng-template ngx-datatable-cell-template let-row="row" let-rowIndex="rowIndex" let-isSelected="isSelected" let-onCheckboxChangeFn="onCheckboxChangeFn">
        <input
          type="checkbox"
          [checked]="isSelected"
          (change)="onCheckboxChangeFn($event)"
          appRowCheckbox
          [rowIndex]="rowIndex"
          [tableId]="tableInstanceId"
        />
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="actions && !isPrintMode"
      name="Actions"
      [resizeable]="false"
      [frozenLeft]="pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="actionColumnWidth"
      [width]="actionColumnWidth"
      [checkboxable]="!customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable) && !isGrouping"
      [headerCheckboxable]="!customCheckboxes && (isBulkDeleteEnable || isBulkUpdateEnable)"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: actions, reportId: dataTableReportId, tableId: tableId }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="impersonate"
      name="Impersonate User"
      [frozenLeft]="commonService.is_small_screen ? false : pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="140"
      [width]="140"
      [checkboxable]="false"
      [headerClass]="'wrap-header'"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: impersonate }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="notificationStatus"
      name="Status"
      [frozenLeft]="commonService.is_small_screen ? false : pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="100"
      [width]="100"
      [minWidth]="100"
      [checkboxable]="false"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header' : ''"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: notificationStatus }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>
    <ngx-datatable-column
      *ngIf="passwordReset"
      name="Password Reset"
      [frozenLeft]="pinActions"
      [draggable]="false"
      [sortable]="false"
      [maxWidth]="140"
      [width]="140"
      [checkboxable]="false"
      [headerCheckboxable]="false"
      [cellClass]="getCellClass"
      [headerClass]="(isColumnFilter || isColumnSearch) ? 'action-header wrap-header' : 'wrap-header'"
    >
      <ng-template let-column="column" ngx-datatable-header-template>{{ column.name }}</ng-template>
      <ng-template let-row="row" let-rowIndex="rowIndex" ngx-datatable-cell-template>
        <ng-container
          *ngTemplateOutlet="specialColumnTemplate; context: { row: row, rowIndex: rowIndex, columnTemplate: passwordReset }"
        ></ng-container>
      </ng-template>
    </ngx-datatable-column>

    <!-- Other Columns -->
    <ng-container *ngIf="renderColumn">
      <ngx-datatable-column
        *ngFor="let col of visibleColumns; let colIdx = index; trackBy: trackByColumn"
        [name]="fromRecordTable ? col.name : (col.name | titlecase)"
        [prop]="col.prop || (col.name | lowercase)"
        [width]="getColWidth(col)"
        [minWidth]="getColMinWidth(col)"
        [comparator]="noClientSort"
        [summaryFunc]="enableSummaryRow && (col.fieldType == 'INT' || col.fieldType === 'DECIMAL(20,2)' || col.fieldType === 'DOUBLE') ? (summaryFunction == 'sum' ? totalSum : summaryFunction == 'avg' ? columnsAvg : totalCount) : nullSummary"
      >
        <ng-template
          let-column="column"
          let-row="row"
          let-value="value"
          let-sort="sortFn"
          ngx-datatable-header-template
        >
          <div style="display: flex; flex-direction: column; width: fit-content;" [ngStyle]="{
            textAlign: col.valueAlign,
            'align-items': col.valueAlign
          }">
            <div class="sort-header-wrapper" [attr.aria-label]="getSortAriaValue(column.prop, column.name)">
              <span class="column-name" (click)="sort()">{{ column.name }}</span>
              <button
                type="button"
                class="sort-icon-button"
                (click)="sort()"
                [attr.aria-label]="'Sort by ' + column.name + ', currently ' + getSortStateText(column.prop)"
              >
                <span
                  class="sort-icon"
                  aria-hidden="true"
                  [ngClass]="sortingColumn[column.prop] === 'asc' ? 'sort-asc datatable-icon-up' : (sortingColumn[column.prop] === 'desc' ? 'datatable-icon-down sort-desc' : 'datatable-icon-sort-unset')"
                ></span>
                <app-sr-only>{{ getSortStateText(column.prop) }}</app-sr-only>
              </button>
            </div>
            <ng-container *ngIf="isColumnSearch">
              <ng-container *ngIf="col.fieldType === 'PII2' || col.fieldType === 'PII' || col.proxyFieldType === 'PII2' || col.proxyFieldType === 'PII'">
                <span *ngIf="!col.allowDecrypt && !col.proxyDecryptionAllowed" style="margin-top: 10px; color: #ffd8d8;" role="alert">Search Not Allowed</span>
                <ng-container *ngIf="col.allowDecrypt || col.proxyDecryptionAllowed">
                  <label [for]="'search-' + col.fieldName">
                    <app-sr-only>Search {{ col.name }}</app-sr-only>
                  </label>
                  <ng-container *ngTemplateOutlet="inputSelector(col); context: { column: column, value: value, row: row, col: col }"></ng-container>
                </ng-container>
              </ng-container>
              <ng-container *ngIf="col.fieldType !== 'PII2' && col.fieldType !== 'PII' && col.proxyFieldType !== 'PII2' && col.proxyFieldType !== 'PII'">
                <label [for]="'search-' + col.fieldName">
                  <app-sr-only>Search {{ col.name }}</app-sr-only>
                </label>
                <ng-container *ngTemplateOutlet="inputSelector(col); context: { column: column, value: value, row: row, col: col }"></ng-container>
              </ng-container>
            </ng-container>
            <ng-container *ngIf="isColumnFilter">
              <ng-container *ngIf="col.fieldType === 'PII2' || col.fieldType === 'PII' || col.proxyFieldType === 'PII2' || col.proxyFieldType === 'PII' || (col.fieldName === 'throttling' && allowColumnFilter)">
                <span style="margin-top: 10px; color: #ffd8d8;" role="alert">Filter Not Allowed</span>
              </ng-container>
              <ng-container *ngIf="col.fieldType !== 'PII2' && col.fieldType !== 'PII' && col.proxyFieldType !== 'PII2' && col.proxyFieldType !== 'PII' && !(col.fieldName === 'throttling' && allowColumnFilter)">
                <button
                  type="button"
                  #filterBtn
                  [id]="'filter-' + col.fieldName"
                  [attr.aria-label]="'Filter ' + col.name + (isFilterSelected(col) ? ', active' : '')"
                  [attr.aria-pressed]="isFilterSelected(col)"
                  [ngStyle]="{'background-color': isFilterSelected(col) ? 'var(--opengov-primary-blurple)' : ''}"
                  [ngbPopover]="filterBox"
                  triggers="manual"
                  #t="ngbPopover"
                  placement="bottom-left top-left bottom-right top-right"
                  [autoClose]="false"
                  popoverClass="custom-popup"
                  container="body"
                  class="btn btn-outline-secondary rounded filter-button template-button d-flex justify-content-center align-items-center"
                  (click)="onOverLayOpen($event, t, column, value, row, col)"
                  (shown)="onFilterPopoverShown(filterBtn)"
                  (hidden)="onFilterPopoverHidden(filterBtn)"
                  aria-haspopup="dialog"
                  [attr.aria-expanded]="t.isOpen()"
                >
                  <i
                    class="icon-filter-list"
                    style="font-size:25px; position:relative; left:0px"
                    [ngStyle]="{'color': isFilterSelected(col) ? 'var(--foreground-ko)' : (isLightHeader ? 'var(--filter-btn-foreground)' :'var(--foreground-ko)')}"
                    aria-hidden="true"
                  ></i>
                  <app-sr-only>Filter {{ col.name }}</app-sr-only>
                </button>
              </ng-container>
            </ng-container>
          </div>
        </ng-template>
        <ng-template
          let-column="column"
          let-row="row"
          let-value="value"
          let-rowIndex="rowIndex"
          ngx-datatable-cell-template
        >
          <ng-container *ngIf="loadingState">
            <div class="skeleton-cell"></div>
          </ng-container>
          <ng-container *ngIf="!loadingState && rows.length">
            <ng-container
              *ngTemplateOutlet="universalCell;
                context: { column: column, value: value, row: row, col: col, rowIndex: rowIndex, colIdx: colIdx }"
            ></ng-container>
          </ng-container>
        </ng-template>
      </ngx-datatable-column>
    </ng-container>

    <ngx-datatable-footer>
      <ng-template
        ngx-datatable-footer-template
        let-rowCount="rowCount"
        let-pageSize="pageSize"
        let-selectedCount="selectedCount"
        let-curPage="curPage"
        let-offset="offset"
        let-isVisible="isVisible"
      >
        <div class="page-count">
          {{ footerText }}
          <ng-container *ngIf="selectedItems.length">
            ({{ selectedItems.length }} selected)
          </ng-container>
        </div>
        <!-- Custom accessible pagination -->
        <app-datatable-pagination
          *ngIf="showEnteriesDropdown && customPagination"
          [currentPage]="currentPageNumber + 1"
          [totalItems]="isVirtualizationEnabled ? page.totalElements : rowCount"
          [pageSize]="isVirtualizationEnabled ? page.size : pageSize"
          (pageChange)="onPageChange($event)"
        ></app-datatable-pagination>

        <!-- Original ngx-datatable pager -->
        <datatable-pager
          *ngIf="showEnteriesDropdown && !customPagination"
          [pagerLeftArrowIcon]="'datatable-icon-left'"
          [pagerRightArrowIcon]="'datatable-icon-right'"
          [pagerPreviousIcon]="'datatable-icon-prev'"
          [pagerNextIcon]="'datatable-icon-skip'"
          [page]="currentPageNumber + 1"
          [size]="isVirtualizationEnabled ? page.size : pageSize"
          [count]="isVirtualizationEnabled ? page.totalElements : rowCount"
          [hidden]="isVirtualizationEnabled ? !(page.totalElements / page.size > 1) : !(rowCount / pageSize > 1)"
          (change)="isVirtualizationEnabled ? pageListener($event) : table.onFooterPage($event)"
        ></datatable-pager>
      </ng-template>
    </ngx-datatable-footer>
  </ngx-datatable>
</div>

<!-- Universal Cell Template -->
<ng-template #universalCell let-column="column" let-value="value" let-row="row" let-col="col" let-rowIndex="rowIndex" let-colIdx="colIdx">
  <div
    appDatatableCell
    [attr.data-row-index]="rowIndex || 0"
    [attr.data-col-index]="colIdx ?? 0"
    [attr.data-table-id]="tableInstanceId"
    [rowIndex]="rowIndex || 0"
    [colIndex]="colIdx ?? 0"
    [columnName]="col?.name || column?.name || 'Unknown'"
    [cellValue]="value"
    [tableId]="tableInstanceId"
    [isFileLink]="col.isFile && value"
    [isExternalLink]="col.isHyperLink && col.isUrl && value"
    [isLink]="col.isHyperLink && !col.isUrl && !col.isEmail && value"
    [isEmailLink]="col.isEmail && value"
    [isEncrypted]="(col.fieldType === 'PII2' || col.proxyFieldType === 'PII2') && value"
  >
  <ng-container *ngIf="col.isHyperLink">
    <div
      *ngIf="value != null && value != '' && col.isUrl"
      class="w-100"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
    >
      <a
        [href]="routeLink(row, column)"
        target="_blank"
        tabindex="-1"
        [ngClass]="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) ? 'hovered-link' : ''"
      >
        {{ linkText(row, column) }}
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <div
      *ngIf="value != null && value != '' && !col.isUrl && col.fieldType !== 'PII2' && col.proxyFieldType !== 'PII2' && !col.isFile"
      [ngClass]="col.isFile ? '' : 'w-100'"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
    >
      <a
        [routerLink]="routeLink(row, column)"
        tabindex="-1"
        [ngClass]="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && col.isRelation) ? 'hovered-link' : ''"
      >
        <ng-container *ngIf="col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && col.isRelation">
          {{ getValue(column, value, row) }}
        </ng-container>
        <ng-container *ngIf="!(col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') || !col.isRelation">
          <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
        </ng-container>
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && col.isRelation) && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <a
      *ngIf="value != null && value != '' && col.isFile"
      class="link"
      tabindex="-1"
      (click)="fileLink(row, column, col)"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </a>
    <div
      *ngIf="value != null && value != '' && !col.isUrl && !col.isFile && (col.fieldType === 'PII2' || col.proxyFieldType === 'PII2')"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
    <div *ngIf="value == null || value == ''" class="w-100">
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
  </ng-container>
  <ng-container *ngIf="col.isFile">
    <ng-container *ngIf="value != null && value != '' && col.isHyperLink">
      <div class="d-flex">
        <a
          title="View Document"
          aria-label="View Document"
          target="_blank"
          tabindex="-1"
          (click)="fileLink(row, column, col)"
          class="d-flex align-items-center mx-1"
        >
          <i class="icon-view"></i>
        </a>
        <a
          title="Download Document"
          aria-label="Download Document"
          target="_blank"
          tabindex="-1"
          (click)="fileLink(row, column, col, true)"
          class="d-flex align-items-center mx-1"
        >
          <i class="icon-download"></i>
        </a>
      </div>
    </ng-container>
    <a
      *ngIf="value != null && value != '' && !col.isHyperLink"
      class="link"
      tabindex="-1"
      (click)="fileLink(row, column, col)"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </a>
    <div
      *ngIf="(value == null || value == '') && !col.isHyperLink"
      class="w-100"
    >
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </div>
  </ng-container>
  <ng-container *ngIf="!col.isHyperLink && !col.isFile">
    <ng-container *ngIf="selectTemplate(value, column, col)">
      <ng-container *ngTemplateOutlet="selectTemplate(value, column, col); context: { column: column, value: value, row: row, col: col }"></ng-container>
    </ng-container>
  </ng-container>
  </div>
</ng-template>

<!-- Custom Templates -->
<ng-template #invitedUsers let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="invite-users">
    <i class="muted">Invitation sent</i>
  </div>
</ng-template>

<ng-template #activeUsers let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="active-users">
    <app-checkbox
      type="toggle"
      [checked]="value === 'True'"
      (change)="openActivateUserModal($event, row)"
    ></app-checkbox>
  </div>
</ng-template>

<ng-template #emailAlerts let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="active-users">
    <app-checkbox
      *ngIf="row.active !== 'Invited'"
      type="toggle"
      [checked]="value === 'True'"
      (change)="OnEmailAlerts($event, row)"
    ></app-checkbox>
    <div *ngIf="row.active == 'Invited'">-</div>
  </div>
</ng-template>

<ng-template #checkbox let-column="column" let-value="value" let-col="col" let-row="row">
  <div
    class="custom-control custom-checkbox custom-checkbox-new w-100 custom-checkbox-un-01"
    (mouseenter)="onMouseEnter(col, row)"
    (mouseleave)="onMouseLeave()"
  >
    <input
      [checked]="isCheckboxChecked(value)"
      type="checkbox"
      class="custom-control-input"
    />
    <label class="custom-control-label custom-checkbox-label custom-table-checkbox"></label>
    <button
      *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
      type="button"
      tabindex="-1"
      [ngbPopover]="inlineEditor"
      triggers="manual"
      #p="ngbPopover"
      [autoClose]="false"
      popoverClass="custom-popup"
      container="body"
      (click)="openEditModal(p, col, row, value)"
      class="btn btn-sm edit-button"
    >
      <i class="icon-edit"></i>
    </button>
  </div>
</ng-template>

<ng-template #default let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    *ngIf="!col.isHtmlallowed"
    [ngStyle]="{'textAlign': col.valueAlign}"
    class="w-100"
    [matTooltip]="getValue(column, value, row)"
    (mouseenter)="onMouseEnter(col, row)"
    (mouseleave)="onMouseLeave()"
  >
    <ng-container *ngIf="col.isEmail">
      <a
        (click)="handleMailClick(getValue(column, value, row))"
        tabindex="-1"
        [ngClass]="{
          'hovered-link email-link': (col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id),
          'active-email': getValue(column, value, row),
          'no-email': !getValue(column, value, row)
        }"
      >
        {{ getValue(column, value, row) || '-' }}
      </a>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </ng-container>
    <ng-container *ngIf="!col.isEmail">
      <span
        [ngClass]="{'hovered-link': (col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true' && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)}"
      >
        {{ getValue(column, value, row) || '-' }}
      </span>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </ng-container>
  </div>
  <div
    *ngIf="col.isHtmlallowed"
    [ngStyle]="{'textAlign': col.valueAlign}"
    [innerHTML]="getValue(column, value, row) | safeHTML"
  ></div>
</ng-template>

<ng-template #fieldsreports let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    [ngStyle]="{'textAlign': col.valueAlign}"
    [matTooltip]="getValue(column, value, row)"
    [class]="getColumnClass(column, value, row)"
  >
    {{ getValue(column, value, row) }}
  </div>
</ng-template>

<ng-template #updateActiveTemplate let-modal>
  <div class="modal-content">
    <div class="modal-header">
      <div class="modal-header-left">
        <h4 class="modal-title">Activate/Deactivate User</h4>
      </div>
      <div class="modal-header-right">
        <button type="button" class="close" (click)="modal.close()">
          <i class="icon-close"></i>
        </button>
      </div>
    </div>
    <div class="modal-body">
      <div class="modal-body-inner">
        <button class="btn btn-dark mr-4" type="button" (click)="updateActivateUser(false)">{{buttonText}} user for this App</button>
        <button class="btn btn-dark" type="button" (click)="updateActivateUser(true)">{{buttonText}} user for all Apps</button>
      </div>
    </div>
    <div class="modal-footer">
      <div class="modal-footer-bottom">
        <app-modal-action-btns
          (onReject)="modal.close()"
          [showSave]="false"
        ></app-modal-action-btns>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #specialColumnTemplate let-row="row" let-rowIndex="rowIndex" let-columnTemplate="columnTemplate" let-reportId="reportId" let-tableId="tableId">
  <div
    appActionContainer
    [rowIndex]="rowIndex"
    [tableId]="tableInstanceId"
  >
    <ng-container *ngIf="loadingState">
      <div class="skeleton-cell"></div>
    </ng-container>
    <ng-container *ngIf="!loadingState">
      <ng-container
        *ngIf="(groupedByColumn || summary?.GroupBy || embeddedGroupColumn) && row?.isgrouped == 'true'"
      >
        Total ({{ row?.tot ? (row?.tot + ' records') : 'records' }})
      </ng-container>
      <ng-container
        *ngIf="!(groupedByColumn || summary?.GroupBy || embeddedGroupColumn) || row?.isgrouped == 'false'"
      >
        <ng-container
          *ngTemplateOutlet="columnTemplate; context: { row: row, rowIndex: rowIndex, reportId: reportId, tableId: tableId, currentPage: currentPageNumber, pageSize: page.size }"
        ></ng-container>
      </ng-container>
    </ng-container>
  </div>
</ng-template>

<ng-template #searchInput let-column="column" let-value="value" let-row="row" let-col="col">
  <input
    *ngIf="col.fieldType !== 'VARCHAR(15)'"
    [id]="'search-' + col.fieldName"
    style="height: 43px;"
    (keyup)="onColumnSearch($event, column, col)"
    (change)="onColumnSearch($event, column, col)"
    placeholder="{{ isColumnFilter ? 'Filter' : 'Search' }} {{ column.name }}"
    class="col-search-ip form-control"
    [ngClass]="{'is-invalid': dirty.includes(column.name)}"
    [type]="inputFieldType(col)"
  />
  <input
    *ngIf="col.fieldType === 'VARCHAR(15)'"
    [id]="'search-' + col.fieldName"
    (keyup.enter)="currentPageNumber = 0; emitPayload()"
    style="height: 43px;"
    (keyup)="onColumnSearch($event, column, col)"
    placeholder="{{ isColumnFilter ? 'Filter' : 'Search' }} {{ column.name }}"
    class="col-search-ip"
    type="text"
    [mask]="col.fieldType === 'VARCHAR(15)' ? '(000) 000-0000' : ''"
  />
</ng-template>

<ng-template #selectSearchScroll let-column="column" let-value="value" let-row="row" let-col="col">
  <app-select-search-scroll
    [minSearchLength]="3"
    [searchable]="true"
    [scrollable]="true"
    class="w-100"
    (selected)="onColumnSearch($event, column, col, true)"
    [isConditionComponent]="(col.isRelation || loadExternalUsers) ? false : true"
    [isSubscriptionComponent]="loadExternalUsers ? true : false"
    [isRelatedDataComponent]="col.isRelation ? true : false"
    [fetchFromServer]="col.isRelation"
    [passedTableKey]="tableId"
    [isSelectDisabled]="true"
    [dropdownFieldKey]="col.isRelation ? col.proxyFieldKey : ''"
    [dropdownType]="col.proxyFieldType"
    [multiple]="isColumnFilter ? true : false"
    [closeOnSelect]="isColumnFilter ? false : true"
    [ngStyle]="{'margin-top': isEmbeddedReport && isInDetailsComponent ? '-8px' : ''}"
    [relatedFieldKey]="col.fieldKey"
  ></app-select-search-scroll>
</ng-template>

<ng-template #enumInput let-column="column" let-value="value" let-row="row" let-col="col">
  <div
    class="form-group select2-form-group w-100"
    style="margin-bottom: 43px;"
    [ngStyle]="{'margin-top': isEmbeddedReport && isInDetailsComponent ? '-8px' : ''}"
  >
    <ng-select
      bindLabel="label"
      bindValue="value"
      [items]="col.dropdownValues"
      [multiple]="(col.fieldType === 'ENUM2' || isColumnFilter) ? true : false"
      appendTo="body"
      (change)="selectSelected($event, column, col)"
      placeholder="Search"
      [closeOnSelect]="isColumnFilter ? false : true"
    >
      <ng-template ng-option-tmp let-item="item">
        {{ item.label }}
      </ng-template>
    </ng-select>
  </div>
</ng-template>

<ng-template #encryptedField let-column="column" let-value="value" let-row="row" let-col="col">
  <div class="d-flex align-items-center w-100">
    <i
      class="icon-view-fill"
      *ngIf="value && (row[col.fieldName + '_decrypted'] || row[col.fieldName + '_proxy_decrypted'])"
      (click)="decryptEncryptField($event, col, row, value)"
      (keydown)="$event.preventDefault()"
    ></i>
    <div
      *ngIf="!col.isHyperLink || !value"
      id="encrypted-{{ col.fieldKey }}-{{ row.id }}"
      [ngStyle]="{'textAlign': col.valueAlign}"
      [matTooltip]="getValue(column, value, row)"
      [class]="getColumnClass(column, value, row)"
      (mouseenter)="onMouseEnter(col, row)"
      (mouseleave)="onMouseLeave()"
      [ngStyle]="{'width': ((allowInlineEdit && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && !col.isHyperLink && value) ? '100%' : 'auto'}"
    >
      <span
        [ngClass]="((allowInlineEdit && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id) && !col.isHyperLink) ? 'hovered-link' : ''"
      >{{ getValue(column, value, row) || '-' }}</span>
      <button
        *ngIf="((col.isEditAllowed && row.isEdit && row?.isgrouped !== 'true') && isMouseOnLink === (col.fieldKey + row.id)) || isEditModalActive === (col.fieldKey + row.id)"
        type="button"
        tabindex="-1"
        [ngbPopover]="inlineEditor"
        triggers="manual"
        #p="ngbPopover"
        [autoClose]="false"
        popoverClass="custom-popup"
        container="body"
        (click)="openEditModal(p, col, row, value)"
        class="btn btn-sm edit-button"
      >
        <i class="icon-edit"></i>
      </button>
    </div>
    <a
      *ngIf="col.isHyperLink && value"
      id="encrypted-{{ col.fieldKey }}-{{ row.id }}"
      [ngStyle]="{'textAlign': col.valueAlign}"
      [routerLink]="routeLink(row, column)"
      tabindex="-1"
      [matTooltip]="getValue(column, value, row)"
      [class]="getColumnClass(column, value, row)"
    >
      {{ getValue(column, value, row) || '-' }}
    </a>
  </div>
</ng-template>

<ng-template #filterBox>
  <app-column-filter
  [filterCol]="filterCol"
  [tableId]="tableId"
  [loadExternalUsers]="loadExternalUsers"
  [allowColumnFilter]="allowColumnFilter"
  (onFilterApply)="selectFilterSelected($event)"
  (onFilterClear)="clearFilterForCol($event)"
></app-column-filter>
</ng-template>

<ng-template #inlineEditor>
  <app-inline-editor
    [col]="editColumn"
    [row]="editRow"
    [value]="editValue"
    [tableId]="tableId"
    (editSubmit)="onEditSubmit($event)"
    (editCancel)="onEditCancel()"
  ></app-inline-editor>
</ng-template>

<!-- Screen reader announcements -->
<div
  role="status"
  aria-live="polite"
  aria-atomic="true"
>
  <app-sr-only>{{ screenReaderMessage }}</app-sr-only>
</div>
</ng-template>
